import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DefaultButton, Dropdown, IDropdownOption, Spinner, SpinnerSize } from '@fluentui/react';
import * as jwt_decode from 'jwt-decode';

import * as CashFlowStatisticsService from '../../../services/CashFlow-Statistics.Services';
import {
    AgingBucketStatisticViewModel,
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    MonthlyCashflowStatisticViewModel,
    OverviewStatisticViewModel,
    PaymentDelayStatisticViewModel,
    PaymentDelaySeriesPointViewModel,
    StatisticsFilter,
    StatisticsTimeSeriesFilter,
    TimeGranularity,
    TopEntityStatisticViewModel
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';

import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import { SponsorSelectorComponent } from '../../../commons/component/Inputs/SponsorSelector/SponsorSelectorComponet';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';

import CashBlockComponent from './CashBlock.Component';
import ProfitabilityBlockComponent from './ProfitabilityBlock.Component';
import ProjectionBlockComponent from './ProjectionBlock.Component';
import * as Functions from './FinancialPanel.Functions';
import { mapDispatchToProps, mapStateToProps, Props } from './FinancialPanel.Redux';

import '../../Contracts/statistics/ContractStatistics.Styles.css';
import './FinancialPanel.Styles.css';

/** Protocolos que se consultan de a una vez para la rentabilidad: rápido sin saturar la API. */
const PROFIT_BATCH = 4;

/** Tope de protocolos a consultar: cada uno es un pedido, y más que esto no se lee igual. */
const MAX_PROFITABILITY_PROTOCOLS = 60;

const GRANULARITY_OPTIONS: IDropdownOption[] = [
    { key: 'Day', text: 'Por día' },
    { key: 'Week', text: 'Por semana' },
    { key: 'Month', text: 'Por mes' },
    { key: 'Quarter', text: 'Por trimestre' },
    { key: 'Year', text: 'Por año' }
];

/** Una llamada que falla se anota con su motivo, en vez de hacerse pasar por "sin datos". */
const settle = async <T,>(label: string, promise: Promise<T[]>, failures: string[]): Promise<T[]> => {
    try {
        return (await promise) ?? [];
    } catch (error: any) {
        const code = error?.ErrorCode ? ` (${error.ErrorCode})` : '';
        const reason = error?.ErrorMessage || error?.Errors?.[0] || error?.message || 'error desconocido';
        failures.push(`${label}${code}: ${reason}`);
        return [];
    }
};

/**
 * Panel financiero del centro.
 *
 * Tres bloques, en el orden en que un director decide: primero la caja —si la plata
 * está o la deben—, después la rentabilidad —qué protocolo la deja y cuál la
 * drena— y al final la proyección —en qué mes no alcanza—. El alcance es todo el
 * centro, y de cada protocolo se baja a su ficha.
 *
 * Cada moneda se mira por separado: la API devuelve una fila por moneda y sumar
 * monedas distintas sin tipo de cambio de cierre no significa nada.
 */
const FinancialPanelComponent: React.FC<Props> = (props) => {
    const { AccessToken, MedicalProtocols, Persons, Practitioners } = props;
    const navigate = useNavigate();

    // ── Filtros ──
    const [startDate, setStartDate] = useState<Date | undefined>(Functions.monthsBack(12));
    const [endDate, setEndDate] = useState<Date | undefined>(Functions.startOfToday());
    const [granularity, setGranularity] = useState<TimeGranularity>('Month');
    const [sponsorId, setSponsorId] = useState<number | undefined>(undefined);
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [refreshTick, setRefreshTick] = useState<number>(0);

    // ── Preferencias de lectura ──
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const [minMargin, setMinMargin] = useState<number>(35);
    const [monthsAhead, setMonthsAhead] = useState<number>(6);
    const [order, setOrder] = useState<Functions.ProfitabilityOrder>('profit');

    // ── Datos de caja ──
    const [overview, setOverview] = useState<OverviewStatisticViewModel[]>([]);
    const [paymentDelay, setPaymentDelay] = useState<PaymentDelayStatisticViewModel[]>([]);
    const [aging, setAging] = useState<AgingBucketStatisticViewModel[]>([]);
    const [series, setSeries] = useState<CashflowSeriesPointViewModel[]>([]);
    const [debtSeries, setDebtSeries] = useState<DebtSeriesPointViewModel[]>([]);
    const [delaySeries, setDelaySeries] = useState<PaymentDelaySeriesPointViewModel[]>([]);
    const [topDebtors, setTopDebtors] = useState<TopEntityStatisticViewModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    // ── Datos de rentabilidad (un pedido por protocolo) ──
    const [profitEntries, setProfitEntries] = useState<Functions.ProtocolCashflow[]>([]);
    const [profitProgress, setProfitProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
    const [profitTruncated, setProfitTruncated] = useState<boolean>(false);

    const baseFilter: StatisticsFilter = useMemo(
        () => ({
            startDate: Functions.isoDate(startDate),
            endDate: Functions.isoDate(endDate),
            sponsorId,
            protocolId
        }),
        [startDate, endDate, sponsorId, protocolId]
    );

    const seriesFilter: StatisticsTimeSeriesFilter = useMemo(
        () => ({ ...baseFilter, granularity }),
        [baseFilter, granularity]
    );

    // Clave estable del filtro: sin esto, el efecto se dispara en cada render.
    const filterKey = useMemo(() => JSON.stringify(seriesFilter), [seriesFilter]);

    useEffect(() => {
        if (MedicalProtocols.length === 0) props.GetAllMedicalProtocols();
        if (Persons.length === 0) props.GetAllPersons();
        if (Practitioners.length === 0) props.GetAllPractitioners(1, 1000, undefined);
        // Sólo al entrar: las listas las mantiene el store.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Carga de la caja ──
    useEffect(() => {
        if (!AccessToken) return;
        let cancelled = false;
        setIsLoading(true);

        const failures: string[] = [];
        Promise.all([
            settle('Resumen', CashFlowStatisticsService.GetOverview(AccessToken, baseFilter), failures),
            settle('Demora de cobro', CashFlowStatisticsService.GetPaymentDelay(AccessToken, baseFilter), failures),
            settle('Antigüedad de deuda', CashFlowStatisticsService.GetAging(AccessToken, baseFilter), failures),
            settle('Serie de caja', CashFlowStatisticsService.GetCashflowSeries(AccessToken, seriesFilter), failures),
            settle('Serie de deuda', CashFlowStatisticsService.GetDebtSeries(AccessToken, seriesFilter), failures),
            settle('Serie de demora', CashFlowStatisticsService.GetPaymentDelaySeries(AccessToken, seriesFilter), failures),
            settle('Mayores deudores', CashFlowStatisticsService.GetTopDebtors(AccessToken, baseFilter, 10), failures)
        ])
            .then(([ov, pd, ag, cs, ds, dls, td]) => {
                if (cancelled) return;
                setOverview(ov as OverviewStatisticViewModel[]);
                setPaymentDelay(pd as PaymentDelayStatisticViewModel[]);
                setAging(ag as AgingBucketStatisticViewModel[]);
                setSeries(cs as CashflowSeriesPointViewModel[]);
                setDebtSeries(ds as DebtSeriesPointViewModel[]);
                setDelaySeries(dls as PaymentDelaySeriesPointViewModel[]);
                setTopDebtors(td as TopEntityStatisticViewModel[]);
                setErrors(failures);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // filterKey resume baseFilter y seriesFilter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccessToken, filterKey, refreshTick]);

    // ── Monedas ──
    const currencies = useMemo(
        () => Functions.availableCurrencies(overview, aging, series),
        [overview, aging, series]
    );

    useEffect(() => {
        const picked = Functions.pickCurrency(currencies, currency);
        if (picked !== currency) setCurrency(picked);
    }, [currencies, currency]);

    const activeCurrency = currency ?? 'ARS';

    /**
     * Rentabilidad: el cashflow de cada protocolo por separado, en tandas. Es un
     * pedido por protocolo porque la API no devuelve el corte por protocolo en una
     * sola consulta; el bloque se va completando a medida que llegan.
     */
    useEffect(() => {
        if (!AccessToken || !currency || MedicalProtocols.length === 0) return;

        const scoped = protocolId
            ? MedicalProtocols.filter((protocol) => protocol.id === protocolId)
            : MedicalProtocols;
        const list = scoped.slice(0, MAX_PROFITABILITY_PROTOCOLS);

        let cancelled = false;
        setProfitEntries([]);
        setProfitTruncated(scoped.length > list.length);
        setProfitProgress({ done: 0, total: list.length });

        const run = async () => {
            const collected: Functions.ProtocolCashflow[] = [];
            for (let index = 0; index < list.length; index += PROFIT_BATCH) {
                const batch = list.slice(index, index + PROFIT_BATCH);
                const results = await Promise.all(
                    batch.map((protocol) =>
                        CashFlowStatisticsService.GetMonthlyCashflow(AccessToken, {
                            ...baseFilter,
                            protocolId: protocol.id,
                            currencyCode: currency
                        })
                            .then((rows: MonthlyCashflowStatisticViewModel[]) => ({ protocol, rows: rows ?? [] }))
                            .catch(() => undefined)
                    )
                );
                if (cancelled) return;
                results.forEach((result) => {
                    if (result) collected.push(result);
                });
                setProfitEntries([...collected]);
                setProfitProgress({ done: Math.min(index + batch.length, list.length), total: list.length });
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccessToken, currency, filterKey, MedicalProtocols, protocolId, refreshTick]);

    // ── Nombre detrás del identificador ──
    const tenantId = useMemo<string | undefined>(() => {
        if (!AccessToken) return undefined;
        try {
            return (jwt_decode.jwtDecode(AccessToken) as { tenantId?: string })?.tenantId;
        } catch {
            return undefined;
        }
    }, [AccessToken]);

    const personsByIdentifier = useMemo(() => {
        const map = new Map<string, string>();
        (Persons || []).forEach((person) => {
            if (person.personIdentifier) map.set(person.personIdentifier.toLowerCase(), person.businessName);
        });
        return map;
    }, [Persons]);

    const practitionersByUserId = useMemo(() => {
        const map = new Map<string, string>();
        (Practitioners || []).forEach((practitioner) => {
            if (practitioner.userId) {
                const display =
                    practitioner.fullName || practitioner.name || practitioner.userName || practitioner.email || practitioner.userId;
                map.set(practitioner.userId.toLowerCase(), display);
            }
        });
        return map;
    }, [Practitioners]);

    const resolveEntityName = useCallback(
        (identifier?: string | null): string => {
            if (!identifier) return '—';
            const lower = identifier.toLowerCase();
            if (tenantId && lower === tenantId.toLowerCase()) return 'El centro';
            return personsByIdentifier.get(lower) || practitionersByUserId.get(lower) || identifier;
        },
        [personsByIdentifier, practitionersByUserId, tenantId]
    );

    // ── Derivados por moneda ──
    const agingRows = useMemo(() => Functions.agingRows(aging, activeCurrency), [aging, activeCurrency]);
    const overviewForCurrency = useMemo(
        () => overview.find((item) => item.currencyCode === activeCurrency),
        [overview, activeCurrency]
    );
    const delayForCurrency = useMemo(
        () => paymentDelay.find((item) => item.currencyCode === activeCurrency),
        [paymentDelay, activeCurrency]
    );
    const seriesForCurrency = useMemo(
        () => series.filter((item) => item.currencyCode === activeCurrency),
        [series, activeCurrency]
    );
    const debtSeriesForCurrency = useMemo(
        () => debtSeries.filter((item) => item.currencyCode === activeCurrency),
        [debtSeries, activeCurrency]
    );
    const delaySeriesForCurrency = useMemo(
        () => delaySeries.filter((item) => item.currencyCode === activeCurrency),
        [delaySeries, activeCurrency]
    );
    const topDebtorsForCurrency = useMemo(
        () => topDebtors.filter((item) => item.currencyCode === activeCurrency).slice(0, 8),
        [topDebtors, activeCurrency]
    );

    const health = useMemo(
        () => Functions.buildCashHealth(overviewForCurrency, delayForCurrency, agingRows, activeCurrency),
        [overviewForCurrency, delayForCurrency, agingRows, activeCurrency]
    );
    const profitabilityRows = useMemo(() => Functions.buildProfitabilityRows(profitEntries), [profitEntries]);
    const projection = useMemo(
        () => Functions.buildProjection(agingRows, health.averageDelayDays, seriesForCurrency, monthsAhead),
        [agingRows, health.averageDelayDays, seriesForCurrency, monthsAhead]
    );

    const handleReset = () => {
        setStartDate(Functions.monthsBack(12));
        setEndDate(Functions.startOfToday());
        setGranularity('Month');
        setSponsorId(undefined);
        setProtocolId(undefined);
    };

    return (
        <div className="cs-container">
            <div className="cs-content">
                {/* ── Filtros ── */}
                <div className="fp-filters">
                    {/* El rango de fechas trae sus propias etiquetas "Desde" y "Hasta":
                        no se le agrega otra encima, se las empareja por estilo. */}
                    <div className="fp-field fp-field--dates">
                        <DateRangeSelectorComponent
                            startDate={startDate}
                            endDate={endDate}
                            onDateChange={(from: Date | undefined, to: Date | undefined) => {
                                setStartDate(from);
                                setEndDate(to);
                            }}
                            startDateLabel="Desde"
                            endDateLabel="Hasta"
                        />
                    </div>

                    <div className="fp-field">
                        <span className="fp-field__label">Agrupar series</span>
                        <Dropdown
                            options={GRANULARITY_OPTIONS}
                            selectedKey={granularity}
                            onChange={(_event, option) => option && setGranularity(option.key as TimeGranularity)}
                        />
                    </div>

                    <div className="fp-field">
                        <span className="fp-field__label">Sponsor</span>
                        <SponsorSelectorComponent
                            selectionLimit={1}
                            onChangeSelection={(selected: any) => {
                                const value = Array.isArray(selected) ? selected[0] : selected;
                                setSponsorId(value?.id);
                            }}
                        />
                    </div>

                    <div className="fp-field">
                        <span className="fp-field__label">Protocolo</span>
                        <ProtocolSelectorComponent
                            selectionLimit={1}
                            onChangeSelection={(selected: any[] | undefined) => setProtocolId((selected || [])[0]?.id)}
                        />
                    </div>

                    <div className="fp-filters__end">
                        {currencies.length > 1 && (
                            <div className="fp-field">
                                <span className="fp-field__label">Moneda</span>
                                <div className="fp-currency">
                                    {currencies.map((item) => (
                                        <button
                                            key={item}
                                            type="button"
                                            className={'fp-currency__btn' + (item === activeCurrency ? ' fp-currency__btn--active' : '')}
                                            onClick={() => setCurrency(item)}
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="fp-filters__actions">
                            <DefaultButton
                                iconProps={{ iconName: 'Refresh' }}
                                text="Actualizar"
                                onClick={() => setRefreshTick(refreshTick + 1)}
                                disabled={isLoading}
                            />
                            <DefaultButton iconProps={{ iconName: 'Reset' }} text="Limpiar" onClick={handleReset} />
                        </div>
                    </div>
                </div>

                {errors.length > 0 && (
                    <div className="cs-cf-error" role="alert">
                        <strong>
                            {errors.length >= 7
                                ? 'No se pudo obtener la información financiera.'
                                : 'Parte de la información no se pudo obtener y falta en el panel.'}
                        </strong>
                        <ul>
                            {errors.map((error) => (
                                <li key={error}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {currencies.length === 0 && !isLoading && errors.length === 0 && (
                    <div className="cs-empty">
                        <span>No hay movimientos de dinero registrados para el período elegido.</span>
                        <span className="cs-empty__hint">Probá ampliando el rango de fechas o quitando los filtros.</span>
                    </div>
                )}

                {/* ── 1. Caja ── */}
                <div className="fp-block">
                    <div className="fp-block__header">
                        <span className="fp-block__number">1</span>
                        <span className="fp-block__title">Caja</span>
                        <span className="fp-block__question">¿Tengo la plata o me la deben?</span>
                    </div>
                    <CashBlockComponent
                        currency={activeCurrency}
                        health={health}
                        aging={agingRows}
                        series={seriesForCurrency}
                        debtSeries={debtSeriesForCurrency}
                        delaySeries={delaySeriesForCurrency}
                        topDebtors={topDebtorsForCurrency}
                        resolveEntityName={resolveEntityName}
                        isLoading={isLoading && overview.length === 0}
                    />
                </div>

                {/* ── 2. Rentabilidad ── */}
                <div className="fp-block">
                    <div className="fp-block__header">
                        <span className="fp-block__number">2</span>
                        <span className="fp-block__title">Rentabilidad</span>
                        <span className="fp-block__question">¿Qué protocolo deja plata y cuál la drena?</span>
                    </div>
                    <ProfitabilityBlockComponent
                        currency={activeCurrency}
                        rows={profitabilityRows}
                        order={order}
                        onOrderChange={setOrder}
                        minMargin={minMargin}
                        onMinMarginChange={setMinMargin}
                        isLoading={profitProgress.done < profitProgress.total}
                        loaded={profitProgress}
                        onProtocolClick={(id) => navigate(`/protocolsagreements/${id}`)}
                    />
                    {profitTruncated && (
                        <div className="cs-empty__hint" style={{ marginTop: 8 }}>
                            Se consultaron los primeros {MAX_PROFITABILITY_PROTOCOLS} protocolos. Filtrá por sponsor o por
                            protocolo para ver el resto.
                        </div>
                    )}
                </div>

                {/* ── 3. Proyección ── */}
                <div className="fp-block">
                    <div className="fp-block__header">
                        <span className="fp-block__number">3</span>
                        <span className="fp-block__title">Proyección</span>
                        <span className="fp-block__question">¿En qué mes me quedo corto?</span>
                    </div>
                    <ProjectionBlockComponent
                        currency={activeCurrency}
                        projection={projection}
                        monthsAhead={monthsAhead}
                        onMonthsAheadChange={setMonthsAhead}
                        dsoDays={health.averageDelayDays}
                    />
                </div>

                {isLoading && (
                    <div className="cs-loading cs-loading--compact">
                        <Spinner size={SpinnerSize.small} />
                        <span>Actualizando…</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FinancialPanelComponent as any);
